import type { AuthGateway } from '@/application/port/out/AuthGateway';
import type { SessionStorageGateway } from '@/application/port/out/SessionStorageGateway';
import type {
  ForgotPasswordCommand,
  LoginCommand,
  LoginResult,
  ResetPasswordCommand,
} from '@/application/port/in/dto/LoginCommand';
import type {
  ForgotPasswordUseCase,
  LoginUseCase,
  LogoutUseCase,
  ResetPasswordUseCase,
} from '@/application/port/in/LoginUseCase';
import { DomainError } from '@/domain/error/DomainError';
import { Email } from '@/domain/valueObject/Email';
import type { AuthSession } from '@/domain/model/User';

function normalizedTenantSlug(value: string): string {
  const slug = value.trim().toLowerCase();
  if (!/^[a-z0-9-]{2,60}$/.test(slug)) throw new DomainError({ code: 'TENANT_SLUG_INVALID', message: 'Informe o código da instituição (2 a 60 letras minúsculas, números ou hífens).', status: 400, fieldErrors: [{ field: 'tenantSlug', code: 'TENANT_SLUG_INVALID', message: 'Código da instituição inválido.' }] });
  return slug;
}

export class LoginUseCaseImpl implements LoginUseCase {
  constructor(
    private readonly authGateway: AuthGateway,
    private readonly storage: SessionStorageGateway,
    private readonly onSessionEstablished: (session: AuthSession) => void,
  ) {}

  async execute(command: LoginCommand): Promise<LoginResult> {
    const tenantSlug = normalizedTenantSlug(command.tenantSlug);
    const emailCheck = Email.validate(command.email);
    if (!emailCheck.valid) {
      throw new DomainError({
        code: 'EMAIL_INVALID',
        message: emailCheck.reason,
        status: 400,
        fieldErrors: [{ field: 'email', code: 'EMAIL_INVALID', message: emailCheck.reason }],
      });
    }
    if (command.password.length === 0) {
      throw new DomainError({
        code: 'PASSWORD_REQUIRED',
        message: 'Senha obrigatória',
        status: 400,
        fieldErrors: [
          { field: 'password', code: 'PASSWORD_REQUIRED', message: 'Senha obrigatória' },
        ],
      });
    }

    try {
      const session = await this.authGateway.login({
        ...command,
        tenantSlug,
        email: emailCheck.email.value,
      });
      this.onSessionEstablished(session);
      // SEMPRE persiste: sem isso, qualquer F5 / Ctrl+Shift+N / nova aba desloga.
      // "Manter conectado" agora afeta apenas TTL do refresh token (backend).
      this.storage.saveRefreshToken(session.refreshToken);
      this.storage.saveSession(session);
      return { kind: 'success' };
    } catch (err) {
      if (err instanceof DomainError && err.code === 'AUTH_REQUIRES_2FA') {
        return { kind: 'requires2fa' };
      }
      throw err;
    }
  }
}

export class LogoutUseCaseImpl implements LogoutUseCase {
  constructor(
    private readonly authGateway: AuthGateway,
    private readonly storage: SessionStorageGateway,
    private readonly onSessionCleared: () => void,
    private readonly getRefreshToken: () => string | null,
  ) {}

  async execute(): Promise<void> {
    const refresh = this.getRefreshToken();
    if (refresh) {
      try {
        await this.authGateway.logout(refresh);
      } catch {
        // Logout best-effort — falha de rede não trava o limpa-sessão local.
      }
    }
    this.storage.clear();
    this.onSessionCleared();
  }
}

export class ForgotPasswordUseCaseImpl implements ForgotPasswordUseCase {
  constructor(private readonly authGateway: AuthGateway) {}

  async execute(command: ForgotPasswordCommand): Promise<void> {
    const tenantSlug = normalizedTenantSlug(command.tenantSlug);
    const emailCheck = Email.validate(command.email);
    if (!emailCheck.valid) {
      throw new DomainError({
        code: 'EMAIL_INVALID',
        message: emailCheck.reason,
        status: 400,
        fieldErrors: [{ field: 'email', code: 'EMAIL_INVALID', message: emailCheck.reason }],
      });
    }
    await this.authGateway.forgotPassword({ tenantSlug, email: emailCheck.email.value });
  }
}

export class ResetPasswordUseCaseImpl implements ResetPasswordUseCase {
  constructor(private readonly authGateway: AuthGateway) {}

  async execute(command: ResetPasswordCommand): Promise<void> {
    if (command.token.trim().length === 0) {
      throw new DomainError({
        code: 'TOKEN_REQUIRED',
        message: 'Token de reset ausente',
        status: 400,
      });
    }
    if (command.newPassword.length < 8) {
      throw new DomainError({
        code: 'PASSWORD_TOO_SHORT',
        message: 'Senha deve ter pelo menos 8 caracteres',
        status: 400,
        fieldErrors: [
          {
            field: 'newPassword',
            code: 'PASSWORD_TOO_SHORT',
            message: 'Senha deve ter pelo menos 8 caracteres',
          },
        ],
      });
    }
    await this.authGateway.resetPassword(command);
  }
}
